/**
 * NetCartas Friends API Service
 *
 * DATE: 2025-12-17 | Author: IA + Luiz Couto
 *
 * Wrapper for NetCartas friend system APIs
 * Used to check friendship status, send invites, and manage friends
 *
 * SECURITY NOTE: All API calls go through the Worker proxy (/api/netcartas/*)
 * The API token is stored securely on the server side and NEVER exposed to the client.
 *
 * TODO [SECURITY]: Implementar autenticação real do usuário.
 * Atualmente confiamos no netcartasId enviado pelo client (MVP).
 * Para produção, validar sessão/token do NetCartas no backend.
 */

// ============================================================================
// CONFIGURAÇÃO
// ============================================================================

/**
 * URL base do Worker proxy que faz chamadas seguras para a API do NetCartas
 * O token de autenticação é adicionado pelo servidor, nunca exposto no client.
 */
const WORKER_API_BASE = import.meta.env.DEV
  ? 'http://localhost:8881/api/netcartas'
  : 'https://domino-server.pixlabs.workers.dev/api/netcartas';

// ============================================================================
// TIPOS
// ============================================================================

/**
 * Informações de um amigo na lista
 */
export interface FriendInfo {
  id: number;               // NetCartas ID
  name: string;             // Nickname
  fullName?: string;
  profilePhoto?: string;
  member?: boolean;
  online?: boolean;
}

/**
 * Resultado do envio de convite de amizade
 */
export interface FriendInviteResult {
  success: boolean;
  saidId?: number;          // ID do convite (para aceitar/recusar)
  error?: string;
}

/**
 * Convite de amizade pendente
 */
export interface PendingFriendInvite {
  saidId: number;           // ID do convite
  fromUserId: number;       // Quem enviou
  fromUserName: string;     // Nome de quem enviou
  toUserId: number;         // Para quem foi enviado
  toUserName: string;       // Nome do destinatário
  createdAt?: string;       // Data de criação
}

/**
 * Status de amizade entre dois usuários
 */
export type FriendshipStatus =
  | 'friends'               // Já são amigos
  | 'pending_sent'          // Convite enviado por mim
  | 'pending_received'      // Convite recebido de outro
  | 'not_friends';          // Não são amigos

// ============================================================================
// FUNÇÕES DE VERIFICAÇÃO
// ============================================================================

/**
 * Verifica se dois usuários são amigos
 *
 * @param myUserId - Meu ID no NetCartas
 * @param targetUserId - ID do usuário alvo
 * @returns Status da amizade
 */
export async function checkFriendshipStatus(
  myUserId: number,
  targetUserId: number
): Promise<FriendshipStatus> {
  try {
    console.log(`🔍 [2025-12-17] Verificando amizade entre ${myUserId} e ${targetUserId}`);

    // Buscar minha lista de amigos
    const friendsList = await getFriendsList(myUserId);

    // Verificar se o alvo está na lista
    const isFriend = friendsList.some(friend => friend.id === targetUserId);
    if (isFriend) {
      console.log(`✅ [2025-12-17] ${myUserId} e ${targetUserId} são amigos`);
      return 'friends';
    }

    // Verificar convites pendentes enviados por mim
    const sentInvites = await getSentInvites(myUserId);
    const sentToTarget = sentInvites.some(invite => invite.toUserId === targetUserId);
    if (sentToTarget) {
      console.log(`📤 [2025-12-17] Convite pendente enviado para ${targetUserId}`);
      return 'pending_sent';
    }

    // Verificar convites pendentes recebidos
    const receivedInvites = await getReceivedInvites(myUserId);
    const receivedFromTarget = receivedInvites.some(invite => invite.fromUserId === targetUserId);
    if (receivedFromTarget) {
      console.log(`📥 [2025-12-17] Convite pendente recebido de ${targetUserId}`);
      return 'pending_received';
    }

    console.log(`❌ [2025-12-17] ${myUserId} e ${targetUserId} não são amigos`);
    return 'not_friends';

  } catch (error) {
    console.error(`❌ [2025-12-17] Erro ao verificar amizade:`, error);
    return 'not_friends';
  }
}

// ============================================================================
// FUNÇÕES DE LISTA
// ============================================================================

/**
 * Busca a lista de amigos de um usuário
 *
 * @param userId - ID do usuário no NetCartas
 * @returns Lista de amigos
 */
export async function getFriendsList(userId: number): Promise<FriendInfo[]> {
  try {
    console.log(`📋 [2025-12-17] Buscando lista de amigos para userId: ${userId}`);

    // Chamada via Worker proxy (token adicionado pelo servidor)
    const response = await fetch(`${WORKER_API_BASE}/friends/list?userId=${userId}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      console.error(`❌ [2025-12-17] Erro ao buscar amigos: ${response.status}`);
      return [];
    }

    const friends: FriendInfo[] = await response.json();
    console.log(`✅ [2025-12-17] ${friends.length} amigos encontrados`);
    return friends;

  } catch (error) {
    console.error(`❌ [2025-12-17] Erro de conexão ao buscar amigos:`, error);
    return [];
  }
}

/**
 * Busca convites de amizade enviados (pendentes)
 *
 * @param userId - ID do usuário no NetCartas
 * @returns Lista de convites enviados
 */
export async function getSentInvites(userId: number): Promise<PendingFriendInvite[]> {
  try {
    console.log(`📤 [2025-12-17] Buscando convites enviados por userId: ${userId}`);

    // Chamada via Worker proxy (token adicionado pelo servidor)
    const response = await fetch(`${WORKER_API_BASE}/friends/invites/sent?userId=${userId}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      console.error(`❌ [2025-12-17] Erro ao buscar convites enviados: ${response.status}`);
      return [];
    }

    const invites: PendingFriendInvite[] = await response.json();
    console.log(`✅ [2025-12-17] ${invites.length} convites enviados encontrados`);
    return invites;

  } catch (error) {
    console.error(`❌ [2025-12-17] Erro de conexão ao buscar convites enviados:`, error);
    return [];
  }
}

/**
 * Busca convites de amizade recebidos (pendentes)
 *
 * @param userId - ID do usuário no NetCartas
 * @returns Lista de convites recebidos
 */
export async function getReceivedInvites(userId: number): Promise<PendingFriendInvite[]> {
  try {
    console.log(`📥 [2025-12-17] Buscando convites recebidos por userId: ${userId}`);

    // Chamada via Worker proxy (token adicionado pelo servidor)
    const response = await fetch(`${WORKER_API_BASE}/friends/invites/received?userId=${userId}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      console.error(`❌ [2025-12-17] Erro ao buscar convites recebidos: ${response.status}`);
      return [];
    }

    const invites: PendingFriendInvite[] = await response.json();
    console.log(`✅ [2025-12-17] ${invites.length} convites recebidos encontrados`);
    return invites;

  } catch (error) {
    console.error(`❌ [2025-12-17] Erro de conexão ao buscar convites recebidos:`, error);
    return [];
  }
}

// ============================================================================
// FUNÇÕES DE CONVITE
// ============================================================================

/**
 * Envia convite de amizade usando apenas IDs (recomendado)
 *
 * @param fromUserId - ID de quem está enviando
 * @param toUserId - ID do destinatário
 * @param fromNickname - Nickname de quem está enviando (usado para notificar AI)
 * @returns Resultado do envio
 *
 * DATE: 2025-12-18 | Author: IA + Luiz Couto
 * Changed to use /friends/invite-by-id endpoint that looks up nickname automatically
 * The fromNickname is now used to identify the sender when notifying AI players
 */
export async function sendFriendInvite(
  fromUserId: number,
  toUserId: number,
  fromNickname?: string  // Nickname of sender (used to notify AI players)
): Promise<FriendInviteResult> {
  try {
    console.log(`📤 [2025-12-18] Enviando convite de amizade por ID: ${fromUserId} -> ${toUserId} (from: ${fromNickname || 'Unknown'})`);

    // Chamada via Worker proxy (token adicionado pelo servidor)
    // DATE: 2025-12-18 | Usando novo endpoint que busca o nickname automaticamente pelo ID
    // Also sends fromNickname to identify sender when notifying AI players
    const response = await fetch(`${WORKER_API_BASE}/friends/invite-by-id`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        fromUserId,
        toUserId,
        fromNickname
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ [2025-12-18] Erro ao enviar convite: ${response.status} - ${errorText}`);
      return {
        success: false,
        error: `Erro ${response.status}: ${errorText}`
      };
    }

    const result = await response.json();
    console.log(`✅ [2025-12-18] Convite enviado com sucesso, friendNickname: ${result.friendNickname}`);
    return {
      success: true,
      saidId: result.saidId || result.id
    };

  } catch (error) {
    console.error(`❌ [2025-12-18] Erro de conexão ao enviar convite:`, error);
    return {
      success: false,
      error: 'Erro de conexão'
    };
  }
}

/**
 * Responde a um convite de amizade (aceitar ou recusar)
 *
 * DATE: 2025-12-22 | Fixed: API requires userId, friendId, saidId, accept
 *
 * @param userId - ID do usuário que está aceitando/recusando
 * @param friendId - ID do amigo que enviou o convite
 * @param saidId - ID do convite
 * @param accept - true para aceitar, false para recusar
 * @param fromNickname - Nome do amigo (para mensagem de boas-vindas do AI)
 * @returns Sucesso da operação
 */
export async function respondToFriendInvite(
  userId: number,
  friendId: number,
  saidId: number,
  accept: boolean,
  fromNickname?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    console.log(`🤝 [2025-12-17] Respondendo convite ${saidId}: ${accept ? 'ACEITAR' : 'RECUSAR'}`);

    // Chamada via Worker proxy (token adicionado pelo servidor)
    const response = await fetch(`${WORKER_API_BASE}/friends/invites/respond`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        userId,
        friendId,
        saidId,
        accept,
        fromNickname  // Para mensagem de boas-vindas do AICompanion
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ [2025-12-17] Erro ao responder convite: ${response.status} - ${errorText}`);
      return {
        success: false,
        error: `Erro ${response.status}: ${errorText}`
      };
    }

    console.log(`✅ [2025-12-17] Convite ${saidId} ${accept ? 'aceito' : 'recusado'} com sucesso`);
    return { success: true };

  } catch (error) {
    console.error(`❌ [2025-12-17] Erro de conexão ao responder convite:`, error);
    return {
      success: false,
      error: 'Erro de conexão'
    };
  }
}

// ============================================================================
// FUNÇÕES DE REMOÇÃO
// ============================================================================

/**
 * Remove um amigo da lista
 *
 * @param userId - Meu ID no NetCartas
 * @param friendId - ID do amigo a remover
 * @returns Sucesso da operação
 */
export async function removeFriend(
  userId: number,
  friendId: number
): Promise<{ success: boolean; error?: string }> {
  try {
    console.log(`🗑️ [2025-12-17] Removendo amigo ${friendId} da lista de ${userId}`);

    // Chamada via Worker proxy (token adicionado pelo servidor)
    const response = await fetch(`${WORKER_API_BASE}/friends/remove`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        userId,
        friendId
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ [2025-12-17] Erro ao remover amigo: ${response.status} - ${errorText}`);
      return {
        success: false,
        error: `Erro ${response.status}: ${errorText}`
      };
    }

    console.log(`✅ [2025-12-17] Amigo ${friendId} removido com sucesso`);
    return { success: true };

  } catch (error) {
    console.error(`❌ [2025-12-17] Erro de conexão ao remover amigo:`, error);
    return {
      success: false,
      error: 'Erro de conexão'
    };
  }
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Retorna a URL base do Worker proxy para a API do NetCartas
 */
export function getNetCartasFriendsApiBase(): string {
  return WORKER_API_BASE;
}

/**
 * Verifica se a API de friends está acessível (via Worker proxy)
 *
 * @returns true se API está respondendo
 */
export async function checkFriendsApiHealth(): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);

    // Chamada via Worker proxy (token adicionado pelo servidor)
    const response = await fetch(`${WORKER_API_BASE}/friends/list?userId=1`, {
      method: 'GET',
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    // Any response (even error) means API is reachable
    return response.status < 500;

  } catch {
    return false;
  }
}
